import { TableCell, TableSortLabel, type TableCellProps } from '@mui/material';
import { ReactElement, useContext } from 'react';
import { MetricHoverContext, MetricSortContext } from './context';
import { getMetricBackground, getMetricHoverBackground, type MetricKey } from './utils';

interface HeaderCellProps extends TableCellProps {
  metricKey?: MetricKey;
  highlightOnHover?: boolean;
  onMetricClick?: (metricKey: MetricKey) => void;
}

export default function HeaderCell({
  children,
  metricKey,
  highlightOnHover = true,
  onMetricClick,
  sx,
  ...props
}: HeaderCellProps): ReactElement {
  const clickable = metricKey !== undefined;
  const { hoveredMetricKey, setHoveredMetricKey } = useContext(MetricHoverContext);
  const { sortState } = useContext(MetricSortContext);
  const isCellHighlightable = highlightOnHover && metricKey !== undefined;
  const active = isCellHighlightable && hoveredMetricKey === metricKey;
  const isSorted = sortState?.metricKey === metricKey;
  const sortDirection = isSorted && sortState ? sortState.order : 'desc';

  return (
    <TableCell
      {...props}
      onMouseEnter={isCellHighlightable ? () => setHoveredMetricKey(metricKey) : undefined}
      onMouseLeave={isCellHighlightable ? () => setHoveredMetricKey(null) : undefined}
      onClick={metricKey ? () => onMetricClick?.(metricKey) : undefined}
      sx={[
        {
          textAlign: 'center',
          whiteSpace: 'nowrap',
          cursor: clickable ? 'pointer' : undefined,
          bgcolor:
            active && metricKey
              ? getMetricHoverBackground(metricKey)
              : getMetricBackground(metricKey),
          '&:hover .MuiTableSortLabel-icon': clickable ? { opacity: 1 } : undefined,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {metricKey ? (
        <TableSortLabel active={isSorted} direction={sortDirection}>
          {children}
        </TableSortLabel>
      ) : (
        children
      )}
    </TableCell>
  );
}
